import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from "react";
import { Empty } from "../components/states";
import { ReportBoundary } from "../components/reports/Frame";
import { SectionHeading } from "../components/kit";
import { Badge } from "@/components/ui/badge";
import { useViewer } from "../lib/viewer";
import { useHeaderOffset } from "../lib/useHeaderOffset";
import { REPORTS } from "@shared/reports.mjs";

import { OutstandingRequests } from "../components/reports/OutstandingRequests";
import { BackOrdered } from "../components/reports/BackOrdered";
import { LowStock } from "../components/reports/LowStock";
import { ClinicMonthlyCost } from "../components/reports/ClinicMonthlyCost";
import { VendorSpending } from "../components/reports/VendorSpending";
import { TopItems } from "../components/reports/TopItems";
import { InventoryValue } from "../components/reports/InventoryValue";
import { NotReceived } from "../components/reports/NotReceived";

/**
 * The eight reports.
 *
 * All eight, on one scrolling page, rather than eight routes behind a menu.
 * The client rejected a previous tool because staff would not use it, and a
 * report nobody can find is a report nobody runs. Two taps: open Reports, jump
 * to the one you want.
 *
 * Each report loads independently. One failing query renders its own error card
 * and the other seven still work — a single dead report must not take the page
 * down in front of the client.
 *
 * **Owner only, and enforced on the server.** Every query behind these calls
 * `requireOwner`. The gate below is courtesy, not security: it stops a manager
 * seeing eight error cards. Removing it would leak nothing.
 */

/**
 * Which component renders each report — and nothing else.
 *
 * The list itself, its order, the index labels and the titles live in
 * `shared/reports.mjs`, because `tools/permcheck.mjs` and `tools/demo.mjs` read
 * the same file. This page used to carry a second copy of all of it, so a ninth
 * report could be linked here and probed by neither harness.
 *
 * A slug in the manifest with nothing registered here renders one error card
 * naming that report, rather than a blank gap in the page: the throw happens
 * inside `ReportBoundary`, which is what keeps it from taking the other seven
 * down with it.
 */
const COMPONENTS: Record<string, () => ReactElement> = {
  "outstanding-requests": OutstandingRequests,
  "back-ordered": BackOrdered,
  "not-received": NotReceived,
  "low-stock": LowStock,
  "inventory-value": InventoryValue,
  "clinic-monthly-cost": ClinicMonthlyCost,
  "vendor-spending": VendorSpending,
  "top-items": TopItems,
};

/**
 * The page's spine.
 *
 * Eight cards in one undifferentiated two-column grid is a wall: nothing tells
 * the reader which of them is a problem and which is a ledger, so all eight get
 * read at the same weight, which means none of them do. The three groups are
 * the three questions an owner actually opens this page with, in that order.
 *
 * **Every report still renders, always.** Grouping here is headings and order —
 * no collapsing, no tabs, nothing that unmounts a report. `tools/demo.mjs`
 * checkbox 7 waits for all eight `data-report-state="ready"` attributes to be
 * present on the page at the same moment, and a report inside a closed panel is
 * a report that is not there. It is also why the index below is a row of anchor
 * links rather than a `<Tabs>`: Radix unmounts the inactive tab's content, so a
 * tabbed Reports page would have one report on the page and seven missing.
 *
 * A slug in `shared/reports.mjs` that nobody grouped is appended to the last
 * group rather than dropped, so adding report #9 cannot silently remove it from
 * the page.
 */
const GROUPS: Array<{ title: string; blurb: string; slugs: string[] }> = [
  {
    title: "What is wrong",
    blurb: "Somebody has to act on these.",
    slugs: ["back-ordered", "low-stock"],
  },
  {
    title: "What is in flight",
    blurb: "Asked for, or on its way, and not finished.",
    slugs: ["outstanding-requests", "not-received"],
  },
  {
    title: "What it cost",
    blurb: "Where the money went and what is on the shelves.",
    slugs: ["clinic-monthly-cost", "vendor-spending", "inventory-value", "top-items"],
  },
];

/** The grouping, reconciled against the registry. Order inside a group is the
 *  group's; anything ungrouped lands at the end of the last one. */
function groupedReports(): Array<{
  title: string;
  blurb: string;
  reports: Array<{ slug: string; title: string }>;
}> {
  const known = new Set(GROUPS.flatMap((group) => group.slugs));
  const orphans = REPORTS.filter((report) => !known.has(report.slug));
  return GROUPS.map((group, index) => ({
    title: group.title,
    blurb: group.blurb,
    reports: [
      ...group.slugs
        .map((slug) => REPORTS.find((report) => report.slug === slug))
        .filter((report): report is (typeof REPORTS)[number] => report !== undefined),
      ...(index === GROUPS.length - 1 ? orphans : []),
    ].map(({ slug, title }) => ({ slug, title })),
  }));
}

/**
 * Where the sticky index and the anchor offsets come from.
 *
 * The shell's header is sticky at every width and its height is not a constant:
 * on a phone it carries a second nav strip and a safe-area inset, on the
 * desktop it is 64px. The index has to sit exactly underneath it — an index
 * that scrolls away is an index nobody can reach from report six, which is the
 * whole complaint it was added to answer.
 *
 * So it is measured rather than guessed, and the same measurement feeds the
 * anchor offset: `--report-anchor` is what every report section's
 * `scroll-margin-top` reads, so a jump lands the card's title below both bars
 * instead of behind them.
 *
 * The header half is `useHeaderOffset` — the same measurement the inventory
 * toolbar sticks to. Only the index's own height is measured here, because only
 * this screen has one. No `resize` listener: a viewport change that alters
 * either height alters the observed boxes, so the `ResizeObserver` already
 * fires and the listener was a second path to the same call.
 */
function useStickyIndex(ready: boolean) {
  const navRef = useRef<HTMLElement>(null);
  const top = useHeaderOffset();
  const [nav, setNav] = useState(0);

  useLayoutEffect(() => {
    const rail = navRef.current;
    if (!rail) return;
    const measure = () => {
      const height = Math.round(rail.getBoundingClientRect().height);
      setNav((previous) => (previous === height ? previous : height));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(rail);
    return () => observer.disconnect();
    // `ready` is false for anyone but an owner, who is the only reader the
    // index actually mounts for.
  }, [ready]);

  return { navRef, top, anchor: top + nav + 8 };
}

export function Reports() {
  const viewer = useViewer();
  const { navRef, top, anchor } = useStickyIndex(viewer.role === "owner");

  if (viewer.role !== "owner") {
    return (
      <div className="space-y-4">
        <SectionHeading title="Reports" />
        <Empty
          title="Reports are owner-only"
          body="These aggregate cost and margin across all five clinics, so they are
                restricted to owner accounts. Your own clinic's requests, invoices and
                statements are on their own screens."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeading
        title="Reports"
        subtitle="All five clinics. Nothing here is a nightly rollup."
        /* Teal, because teal is interaction and liveness in this app and never
           a status. The word is the claim the subtitle makes, said once more
           where the eye lands first. */
        action={
          <Badge
            variant="secondary"
            className="bg-primary/10 text-primary ring-1 ring-primary/20 ring-inset"
          >
            Live
          </Badge>
        }
      />

      {/* The index: a row of secondary nav links, which is what shadcn's own
          examples put under a page title — `text-muted-foreground` until you
          reach for one. It was a rail of rounded-full pills on a grey band,
          which is a control this design system does not have.

          Still sticky, and still under the header it measures: eight reports
          deep on a phone, an index you have to scroll back to the top to reach
          is not an index. It scrolls sideways at every width rather than
          wrapping — one behaviour, and eight labels cannot clip against the
          window edge at 700px the way a wrapping row did. */}
      <nav
        ref={navRef}
        style={{ top }}
        className="sticky z-30 -mx-4 overflow-x-auto border-b bg-background px-4 lg:-mx-8 lg:px-8"
        aria-label="Jump to report"
      >
        <div className="flex gap-4 lg:gap-6">
          {REPORTS.map((section) => (
            <a
              key={section.slug}
              href={`#${section.slug}`}
              className="shrink-0 rounded-sm py-3 text-sm font-medium whitespace-nowrap text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {section.label}
            </a>
          ))}
        </div>
      </nav>

      {/* Three groups, two even columns inside each at `lg`, one column at
       * 390px. `--report-anchor` is inherited by every `<section data-report>`
       * below; see `useStickyIndex`. */}
      <div
        style={{ "--report-anchor": `${anchor}px` } as CSSProperties}
        className="space-y-8"
      >
        {groupedReports().map((group) => (
          <section key={group.title} className="space-y-4">
            {/* Dashboard section typography, and no rule under it: every card
                below draws its own edge, so a divider here was a third line of
                chrome saying what the whitespace already said. */}
            <div>
              <h2 className="text-lg font-semibold tracking-tight">
                {group.title}
              </h2>
              <p className="text-sm text-muted-foreground">{group.blurb}</p>
            </div>
            <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
              {group.reports.map(({ slug, title }) => {
                const Component = COMPONENTS[slug];
                return (
                  <div key={slug} className="min-w-0">
                    <ReportBoundary slug={slug} title={title}>
                      {Component ? <Component /> : <Unregistered slug={slug} />}
                    </ReportBoundary>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function Unregistered({ slug }: { slug: string }): never {
  throw new Error(`No component is registered for the report "${slug}"`);
}
